import React, { useState } from 'react';

/**
 * EditableField — click-to-edit inline field. Saves on blur or Enter (Esc cancels).
 * Shared by FirmDetail and the firm widgets.
 * type: 'text' | 'number' | 'date' | 'textarea' | 'select'
 */
export default function EditableField({ value, display, onSave, type = 'text', options = [], placeholder = '—', className = '', inputClassName = '', multiline = false, saving = false }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value ?? '');
    const inputRef = React.useRef(null);

    React.useEffect(() => { if (editing) { setDraft(value ?? ''); } /* eslint-disable-next-line */ }, [editing]);
    React.useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
            if (inputRef.current.select && type !== 'date') inputRef.current.select();
        }
    }, [editing, type]);

    const commit = () => {
        setEditing(false);
        const normalized = typeof draft === 'string' ? draft.trim() : draft;
        if (String(normalized ?? '') !== String(value ?? '')) onSave(normalized === '' ? null : normalized);
    };
    const cancel = () => { setEditing(false); setDraft(value ?? ''); };

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !multiline) { e.preventDefault(); commit(); }
        else if (e.key === 'Enter' && multiline && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commit(); }
        else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
    };

    if (editing) {
        if (type === 'select') {
            return (
                <select ref={inputRef} value={draft ?? ''} onChange={e => setDraft(e.target.value)} onBlur={commit} onKeyDown={onKeyDown}
                    className={`text-sm border border-emerald-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${inputClassName}`}>
                    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
            );
        }
        if (multiline) {
            return (
                <textarea ref={inputRef} value={draft ?? ''} onChange={e => setDraft(e.target.value)} onBlur={commit} onKeyDown={onKeyDown}
                    rows={4} placeholder={placeholder}
                    className={`w-full text-sm border border-emerald-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${inputClassName}`} />
            );
        }
        return (
            <input ref={inputRef} type={type} value={draft ?? ''} onChange={e => setDraft(e.target.value)} onBlur={commit} onKeyDown={onKeyDown}
                placeholder={placeholder}
                className={`text-sm border border-emerald-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${inputClassName}`} />
        );
    }

    return (
        <button type="button" onClick={() => setEditing(true)}
            className={`group/edit text-left rounded px-1 -mx-1 hover:bg-emerald-50/70 transition-colors inline-flex items-center gap-1 ${className}`}
            title="Click to edit">
            <span className={value == null || value === '' ? 'text-gray-400' : ''}>{display ?? (value || placeholder)}</span>
            {saving
                ? <span className="w-2.5 h-2.5 rounded-full border border-emerald-500 border-t-transparent animate-spin flex-shrink-0" />
                : <svg className="w-3 h-3 text-gray-300 opacity-0 group-hover/edit:opacity-100 transition-opacity flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>}
        </button>
    );
}
